import React, { useState, useEffect, useRef } from 'react'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Box from '@mui/material/Box'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import {
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  Tooltip,
  Button
} from '@mui/material'
import ValidatedDatePicker from '../../components/ui/DatePicker'
import { ValidatorForm } from 'react-material-ui-form-validator'
import axios from '../../axios-order'
import MuiAlert from '@material-ui/lab/Alert'
import BoxWrapper from '../../components/ui/boxWrapper'
import SelectField from '../../components/ui/basicSelectInput'
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from '@mui/x-data-grid-pro'

const toggleButtonStyle = {
  flex: 1,
  border: 0,
  borderRadius: '30px',
  textTransform: 'none',
  px: 3,
  py: 1,
  fontWeight: 600,
  color: '#000000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  whiteSpace: 'nowrap',
  minWidth: '200px',
  '&.Mui-selected': {
    backgroundColor: 'var(--main-btn-color)',
    color: '#ffffff'
  },
  '&:hover': {
    backgroundColor: 'transparent'
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'var(--main-btn-color)'
  }
}

const noticeColumns = (handleDownloadDocument, noticeDetails) => [
  {
    field: 'ref_id',
    headerName: 'Reference ID',
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    filterable: true
  },
  {
    field: 'module_description',
    headerName: 'Module Description',
    flex: 1,
    minWidth: 200,
    align: 'center',
    headerAlign: 'center',
    filterable: true
  },
  {
    field: 'date_of_issue',
    headerName: 'Date of Issue',
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    filterable: true
  },
  {
    field: 'notice_type',
    headerName: 'Notice Type',
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    filterable: true
  },
  {
    field: 'due_date_of_reply',
    headerName: 'Due Date',
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    filterable: true
  },
  {
    field: 'main_document',
    headerName: 'Main Document',
    flex: 1,
    minWidth: 180,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: params =>
      params.row.doc_name ? (
        <Button
          variant='contained'
          size='small'
          startIcon={<DownloadIcon />}
          onClick={() =>
            handleDownloadDocument(
              'main',
              params.row.doc_name,
              noticeDetails.summaryId
            )
          }
          sx={{
            backgroundColor: '#03406d',
            textTransform: 'none',
            fontSize: '12px',
            padding: '4px 12px',
            '&:hover': {
              backgroundColor: '#025a8d'
            }
          }}
        >
          Download
        </Button>
      ) : (
        'N/A'
      )
  },
  {
    field: 'supplementary_document',
    headerName: 'Supplementary Document',
    flex: 1,
    minWidth: 180,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: params =>
      params.row.supp_doc_name ? (
        <Button
          variant='contained'
          size='small'
          startIcon={<DownloadIcon />}
          onClick={() =>
            handleDownloadDocument(
              'supplementary',
              params.row.supp_doc_name,
              noticeDetails.summaryId
            )
          }
          sx={{
            backgroundColor: '#03406d',
            textTransform: 'none',
            fontSize: '12px',
            padding: '4px 12px',
            '&:hover': {
              backgroundColor: '#025a8d'
            }
          }}
        >
          Download
        </Button>
      ) : (
        'N/A'
      )
  }
]

const CustomToolbar = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'flex-start',
      width: '100%',
      height: '60px',
      flexWrap: 'wrap',
      gap: 1,
      padding: '3px 10px',
      backgroundColor: '#ffffff',
      borderRadius: '4px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    }}
  >
    <GridToolbarColumnsButton
      sx={{
        borderColor: '#1976d2',
        color: '#1976d2',
        borderRadius: '4px',
        padding: '2px 4px',
        '&:hover': { borderColor: '#1565c0', backgroundColor: '#e3f2fd' }
      }}
    />
    <GridToolbarFilterButton
      sx={{
        borderColor: '#1976d2',
        color: '#1976d2',
        borderRadius: '4px',
        padding: '2px 4px',
        '&:hover': { borderColor: '#1565c0', backgroundColor: '#e3f2fd' }
      }}
    />
    <GridToolbarDensitySelector
      sx={{
        borderColor: '#1976d2',
        color: '#1976d2',
        borderRadius: '4px',
        padding: '2px 4px',
        '&:hover': { borderColor: '#1565c0', backgroundColor: '#e3f2fd' }
      }}
    />
  </Box>
)

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      fontSize: '16px',
      fontWeight: 'medium',
      color: 'text.secondary'
    }}
  >
    No Data Available
  </Box>
)

function NoticeManagement() {
  const isOrderEnabled = false;
  const isAdditionalNoticesEnabled = false;

  const [value, setValue] = useState('notices')
  const [noticeType, setNoticeType] = useState('')
  const [issuancePeriod, setIssuancePeriod] = useState(null)
  const form = useRef('form')

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [responseMessage, setResponseMessage] = useState('')
  const [noticeHistoryData, setNoticeHistoryData] = useState([])
  const [loadingNoticeId, setLoadingNoticeId] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [noticeDetails, setNoticeDetails] = useState(null)
  const [branchList, setBranchList] = useState([])
  const [selectedBranch, setSelectedBranch] = useState('')
  const [isSelectFieldMandatory, setIsSelectFieldMandatory] = useState(false)
  const [entityType, setEntityType] = useState('')
  const [branchData, setBranchData] = useState([])

  useEffect(() => {
    const business_entity_id = localStorage.getItem('business_entity_id')
    const entity_type = localStorage.getItem('entity_type')
    setEntityType(entity_type)

    axios
      .get(`/multimonth_get_api/${business_entity_id}`)
      .then(res => {
        const branchData = res.data
        setBranchData(branchData)

        const entityChoices = branchData.map(entity => ({
          value: entity.gstin,
          label: entity.entity_name
        }))
        setBranchList(entityChoices)
        if (entity_type === 'Group') {
          setIsSelectFieldMandatory(true)
        } else {
          setIsSelectFieldMandatory(false)
        }
        if (entity_type === 'Main') {
          setSelectedBranch(branchData[0]?.gstin || '')
        }
      })
      .catch(error => {
        console.error('Error fetching branch data:', error)
      })
  }, [])

  const handleIssuancePeriodChange = v => {
    setIssuancePeriod(v)
  }

  const handleChange = (event, newValue) => {
    if (!newValue) return
    setValue(newValue)
  }

  const handleNoticeTypeChange = event => {
    setNoticeType(event.target.value)
  }

  const handleSnackbarOpen = (message, severity) => {
    setSnackbarOpen(true)
    setSnackbarSeverity(severity)
    setResponseMessage(message)
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
    setSnackbarSeverity('success')
    setResponseMessage('')
  }

  const handleBackToList = () => {
    setShowDetails(false)
    setNoticeDetails(null)
  }

  const handleViewNotice = async item => {
    try {
      const summaryId =
        item.id || item.summary_id || item.notice_id || item.log_id
      const businessEntityId =
        item.business_entity_id || localStorage.getItem('business_entity_id')

      if (!summaryId) {
        handleSnackbarOpen('Notice ID not found', 'error')
        return
      }

      setLoadingNoticeId(summaryId)

      const response = await axios.get(
        `/get_notices_records/${summaryId}/${businessEntityId}`
      )

      setLoadingNoticeId(null)

      if (response.data.status === true) {
        setNoticeDetails({
          data: response.data.data,
          totalRecords: response.data.total_records,
          summaryId: summaryId,
          noticeInfo: item
        })
        setShowDetails(true)

        handleSnackbarOpen(
          response.data.message || 'Notice details fetched successfully',
          'success'
        )
      } else {
        handleSnackbarOpen(
          response.data.message || 'Failed to fetch notice details',
          'error'
        )
      }
    } catch (error) {
      setLoadingNoticeId(null)
      handleSnackbarOpen(
        error.response?.data?.message || 'Error fetching notice details',
        'error'
      )
      console.error('Error fetching notice details:', error)
    }
  }

  const handleDownloadDocument = async (docType, docName, summaryId) => {
    try {
      const businessEntityId = localStorage.getItem('business_entity_id')

      const payload = {
        summary_id: summaryId,
        business_entity_id: parseInt(businessEntityId),
        doc_type: docType
      }

      const response = await axios.post(
        '/notice_management_api_documents',
        payload
      )

      if (response.data.status) {
        const base64Data = response.data.data?.doc_base64_data
        const fileName = response.data.data?.doc_name || docName
        const contentType =
          response.data.data?.doc_content_type || 'application/pdf'

        if (base64Data) {
          const blob = base64ToBlob(base64Data, contentType)
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          handleSnackbarOpen(
            response.data.message || 'Document downloaded successfully',
            'success'
          )
        } else {
          handleSnackbarOpen('Document data not found in response', 'error')
        }
      } else {
        handleSnackbarOpen(
          response.data.message || 'Failed to download document',
          'error'
        )
      }
    } catch (error) {
      handleSnackbarOpen(
        error.response?.data?.message || 'Error downloading document',
        'error'
      )
      console.error('Error downloading document:', error)
    }
  }

  const base64ToBlob = (base64, contentType) => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: contentType })
  }

  const getNoticeListApi = () => {
    let selectedEntity
    if (entityType === 'Main' || branchData.length === 1) {
      selectedEntity = branchData[0]
    } else {
      selectedEntity = branchData.find(
        entity => entity.gstin === selectedBranch
      )
    }

    const businessEntityId = selectedEntity
      ? selectedEntity.business_entity_id
      : localStorage.getItem('business_entity_id')

    axios
      .get(`/notice_order_summary_logs/${businessEntityId}`)
      .then(res => {
        if (res.data.status === true) {
          setNoticeHistoryData(res?.data?.data || [])
        } else {
          setNoticeHistoryData([])
        }
      })
      .catch(e => {
        setNoticeHistoryData([])
        console.error('Error fetching notice list:', e)
      })
  }

  useEffect(() => {
    if (branchData.length > 0) {
      getNoticeListApi()
    }
  }, [branchData, selectedBranch])

  const onSubmitHandler = () => {
    if (!noticeType) {
      handleSnackbarOpen('Please select a Notice Type', 'error')
      return
    }

    const formatDate = date => {
      if (!date) return ''
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    }

    let selectedEntity
    if (entityType === 'Main' || branchData.length === 1) {
      selectedEntity = branchData[0]
    } else {
      selectedEntity = branchData.find(
        entity => entity.gstin === selectedBranch
      )
    }

    const gstin =
      entityType === 'Main' ? branchData[0]?.gstin : selectedEntity?.gstin

    const payLoadData = {
      gstin: gstin,
      business_entity_id: selectedEntity
        ? selectedEntity.business_entity_id
        : parseInt(localStorage.getItem('business_entity_id')),
      issuance_period: formatDate(issuancePeriod),
      type: noticeType.charAt(0).toUpperCase() + noticeType.slice(1)
    }

    axios
      .post('/notice_management_api', payLoadData)
      .then(res => {
        if (res.data.status) {
          handleSnackbarOpen(
            res.data.message || 'Notice fetched successfully',
            'success'
          )
          setNoticeType('')
          setIssuancePeriod(null)
          getNoticeListApi()
        } else {
          handleSnackbarOpen(
            res.data.message || 'Failed to fetch notice',
            'error'
          )
          getNoticeListApi()
        }
      })
      .catch(err => {
        handleSnackbarOpen(
          err.response?.data?.message || 'An error occurred',
          'error'
        )
        console.error('Error:', err)
        getNoticeListApi()
      })
  }

  if (showDetails && noticeDetails) {
    return (
      <Box>
        <button
          className='button-primary'
          onClick={handleBackToList}
          style={{
            marginBottom: '20px',
            padding: '8px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '18px' }} />
          BACK
        </button>

        <Box sx={{ height: '65vh', width: '100%', marginTop: '4px' }}>
          <DataGridPro
            rows={noticeDetails.data || []}
            columns={noticeColumns(handleDownloadDocument, noticeDetails)}
            getRowId={row => row.ref_id || Math.random()}
            loading={loadingNoticeId !== null}
            disableRowSelectionOnClick
            pagination
            pageSize={25}
            pageSizeOptions={[5, 10, 25, 50]}
            sx={{
              maxHeight: '65vh',
              '& .MuiDataGrid-columnHeaders': {
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                zIndex: 1000
              },
              '& .MuiDataGrid-columnHeader': {
                position: 'relative',
                textAlign: 'center',
                width: '100%',
                fontWeight: 'bold',
                backgroundColor: '#607d8b'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#fff'
              },
              '& .MuiDataGrid-sortIcon': {
                opacity: 'inherit !important'
              },
              '& .MuiDataGrid-iconButtonContainer': {
                visibility: 'visible'
              },
              '& .MuiDataGrid-menuIconButton': {
                opacity: '1 !important',
                visibility: 'visible !important',
                display: 'flex !important'
              },
              '& .MuiDataGrid-columnHeaderIcon': {
                color: '#fff'
              },
              '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                color: '#fff'
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: '#f9f9f9'
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#ffffff'
              },
              '& .MuiDataGrid-cell': {
                padding: '8px',
                textAlign: 'center'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#f0f0f0'
              },
              '& .MuiCheckbox-root': {
                color: '#1976d2'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#d3e3fc',
                cursor: 'pointer'
              }
            }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: CustomNoRowsOverlay
            }}
          />
        </Box>

        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          style={{ maxWidth: '25%' }}
        >
          <MuiAlert
            elevation={6}
            variant='filled'
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
          >
            {responseMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    )
  }
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 3
        }}
      >
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={handleChange}
          aria-label='notice toggle'
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '30px',
            p: '3px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            display: 'flex',
            width: 'fit-content',
            '.MuiToggleButton-root': {
              textTransform: 'uppercase',
              fontWeight: 550,
              fontFamily: '"Dosis", sans-serif',
              letterSpacing: '1px',
              fontSize: '13px'
            }
          }}
        >
          <ToggleButton
            value='notices'
            aria-label='notices'
            sx={toggleButtonStyle}
          >
            Notices and Orders
          </ToggleButton>

          <ToggleButton
            value='additional_notices'
            aria-label='additional_notices'
            sx={toggleButtonStyle}
            disabled={!isAdditionalNoticesEnabled}
          >
            Additional Notices and Orders
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          mt: 3,
          backgroundColor: '#03406d',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '500',
            color: '#fff',
            textAlign: 'center'
          }}
        >
          List of Notices Issued for particular taxpayer within last 60 days
          time period
        </p>
      </Box>

      <ValidatorForm
        ref={form}
        onSubmit={onSubmitHandler}
        onError={errors => console.log(errors)}
      >
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            gap: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}
        >
          {/* Branch Selection - Show only for Group entity type */}
          {entityType === 'Group' && (
            <Box>
              <label
                style={{
                  margin: 0,
                  marginBottom: '8px',
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Select Branch<span style={{ color: 'red' }}>*</span>:
              </label>
              <FormControl sx={{ width: '250px' }}>
                <Select
                  value={selectedBranch}
                  onChange={e => setSelectedBranch(e.target.value)}
                  displayEmpty
                  required={isSelectFieldMandatory}
                  sx={{
                    height: '40px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                >
                  <MenuItem value=''>Select Branch</MenuItem>
                  {branchList.map(branch => (
                    <MenuItem key={branch.value} value={branch.value}>
                      {branch.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box>
            <label
              style={{
                margin: 0,
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px',
                fontWeight: 400
              }}
            >
              Type<span style={{ color: 'red' }}>*</span>:
            </label>
            <FormControl sx={{ width: '200px' }}>
              <Select
                labelId='notice-type-label'
                id='notice-type-select'
                name='noticeType'
                value={noticeType}
                onChange={handleNoticeTypeChange}
                displayEmpty
                sx={{
                  height: '40px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                <MenuItem value=''>Select Type</MenuItem>
                <MenuItem value='notice'>Notice</MenuItem>
                <MenuItem value='order' disabled={!isOrderEnabled}>Order</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <label
              style={{
                margin: 0,
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px',
                fontWeight: 400
              }}
            >
              Issuance Period<span style={{ color: 'red' }}>*</span>:
            </label>
            <ValidatedDatePicker
              name='issuance_period'
              placeholder='DD/MM/YYYY'
              value={issuancePeriod}
              onChange={v => {
                handleIssuancePeriodChange(v)
              }}
              validators={['required']}
              errorMessages={['This field is required']}
            />
          </Box>
          <Box sx={{ mt: 4 }}>
            <button
              className='button-primary'
              type='submit'
              style={{ height: '40px', padding: '0 20px' }}
            >
              Submit
            </button>
          </Box>
        </Box>
      </ValidatorForm>
      <BoxWrapper boxTitle='Notice History'>
        <div className='responsive-table'>
          <table className='custome-table'>
            <thead>
              <tr>
                <th>Type</th>
                <th>Issuance Period</th>
                <th>Uploaded Date & Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {noticeHistoryData?.length > 0 ? (
                noticeHistoryData.map((item, i) => (
                  <tr key={`${i}notice`}>
                    <td>{item?.type || 'N/A'}</td>
                    <td>{item?.issuance_period || 'N/A'}</td>
                    <td>{item?.uploaded_datetime || 'N/A'}</td>
                    <td>
                      <Tooltip title={
                        item?.remarks === "No notices found" || item?.remarks === "FAILED: API returned 400"
                          ? "No notices available"
                          : "View Details"
                      }>
                        <span>
                        <IconButton
                          onClick={() => handleViewNotice(item)}
                          size='small'
                          disabled={
                              loadingNoticeId === (item.id || item.summary_id) ||
                              item?.remarks === "No notices found" ||
                              item?.remarks === "FAILED: API returned 400"
                          }
                          sx={{
                            color: '#03406d',
                            '&:hover': {
                              backgroundColor: 'rgba(3, 64, 109, 0.1)'
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)'
                            }
                          }}
                        >
                          {loadingNoticeId === (item.id || item.summary_id) ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: '#03406d' }}
                            />
                          ) : (
                            <VisibilityOutlinedIcon fontSize='small' />
                          )}
                        </IconButton>
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'red' }}>
                    No Data Found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </BoxWrapper>

      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        style={{ maxWidth: '25%' }}
      >
        <MuiAlert
          elevation={6}
          variant='filled'
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
        >
          {responseMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}

export default NoticeManagement
